/**
 * AttentionCard — Card de "Pontos que requerem atenção" no Dashboard.
 *
 * Lê do NotificationsContext (que faz polling automático). Mostra top 5.
 * Cada item tem ações inline: Ver / Adiar 7d / Resolver.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle, ArrowRight, Check, Clock, Package, Users, Calendar,
  DollarSign, MessageSquare, Sparkles,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNotifications } from "@/contexts/NotificationsContext";

const CATEGORY_META = {
  estoque: { label: "Estoque", icon: Package, tone: "text-emerald-700 bg-emerald-500/10 border-emerald-500/30" },
  clientes: { label: "Clientes", icon: Users, tone: "text-blue-700 bg-blue-500/10 border-blue-500/30" },
  agenda: { label: "Agenda", icon: Calendar, tone: "text-amber-700 bg-amber-500/10 border-amber-500/30" },
  financeiro: { label: "Financeiro", icon: DollarSign, tone: "text-purple-700 bg-purple-500/10 border-purple-500/30" },
  whatsapp: { label: "WhatsApp", icon: MessageSquare, tone: "text-green-700 bg-green-500/10 border-green-500/30" },
};

const PRIORITY_STYLE = {
  high: "bg-red-500/10 text-red-700 border-red-500/30",
  medium: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  low: "bg-zinc-500/10 text-zinc-700 border-zinc-500/30",
};
const PRIORITY_LABEL = { high: "Alta", medium: "Média", low: "Baixa" };

export default function AttentionCard() {
  const nav = useNavigate();
  const { items, total, loaded, resolve, snooze } = useNotifications();
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(false);

  if (!loaded) return null;

  const top = expanded ? items : items.slice(0, 5);

  const handleResolve = async (id) => {
    setBusy(id);
    try { await resolve(id); toast.success("Marcado como resolvido"); }
    catch { toast.error("Erro ao resolver"); }
    finally { setBusy(null); }
  };

  const handleSnooze = async (id) => {
    setBusy(id);
    try { await snooze(id, 7); toast.success("Adiado por 7 dias"); }
    catch { toast.error("Erro ao adiar"); }
    finally { setBusy(null); }
  };

  return (
    <section className="space-y-3" data-testid="section-attention">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-tenant" strokeWidth={1.8} />
            Pontos que requerem sua atenção
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {total === 0 ? "Tudo em dia — nenhum ponto pendente."
              : `${total} sugestão(ões) do sistema para você agir hoje`}
          </p>
        </div>
        {items.length > 5 && (
          <button
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            onClick={() => setExpanded((v) => !v)}
            data-testid="attention-toggle-expand"
          >
            {expanded ? "Mostrar apenas 5" : `Ver todas (${items.length})`}
            <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>

      <Card className="rounded-tenant border-border overflow-hidden">
        {total === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center flex flex-col items-center gap-2">
            <Check className="w-6 h-6 text-emerald-600" />
            Você está no controle. Sem pontos de atenção agora. 🎉
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {top.map((n) => {
              const meta = CATEGORY_META[n.category] || CATEGORY_META.estoque;
              const Icon = meta.icon;
              const primaryAction = (n.quick_actions || [])[0];
              return (
                <li
                  key={n.id}
                  className="p-4 hover:bg-muted/30 transition-colors"
                  data-testid={`attention-item-${n.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-md border grid place-items-center shrink-0 ${meta.tone}`}>
                      <Icon className="w-4 h-4" strokeWidth={1.7} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-2 mb-1">
                        <div className="text-sm font-medium leading-tight flex-1 min-w-0">{n.title}</div>
                        <Badge variant="outline" className={`${PRIORITY_STYLE[n.priority]} text-[10px] shrink-0`}>
                          {PRIORITY_LABEL[n.priority] || n.priority}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mb-2">{n.message}</div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {primaryAction && (
                          <Button
                            size="sm" variant="default"
                            className="h-7 text-xs gap-1"
                            onClick={() => nav(primaryAction.url)}
                            data-testid={`attention-action-view-${n.id}`}
                          >
                            {primaryAction.label} <ArrowRight className="w-3 h-3" />
                          </Button>
                        )}
                        <Button
                          size="sm" variant="ghost"
                          className="h-7 text-xs gap-1"
                          onClick={() => handleSnooze(n.id)}
                          disabled={busy === n.id}
                          data-testid={`attention-snooze-${n.id}`}
                        >
                          <Clock className="w-3 h-3" /> Adiar 7d
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          className="h-7 text-xs gap-1 text-emerald-700 hover:text-emerald-700 hover:bg-emerald-500/10"
                          onClick={() => handleResolve(n.id)}
                          disabled={busy === n.id}
                          data-testid={`attention-resolve-${n.id}`}
                        >
                          <Check className="w-3 h-3" /> Resolver
                        </Button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </section>
  );
}
